import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Session timeout in milliseconds (15 minutes)
const SESSION_TIMEOUT = 15 * 60 * 1000;
// Warning before timeout (2 minutes before)
const WARNING_BEFORE_TIMEOUT = 2 * 60 * 1000;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());
  // Ref mirror of lastActivity so the token-refresh interval can read the
  // latest activity time WITHOUT re-creating the interval on every mouse move.
  const lastActivityRef = useRef(Date.now());
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [viewingAs, setViewingAs] = useState(null);

  // Set up axios interceptor for auth headers
  useEffect(() => {
    const interceptor = axios.interceptors.request.use(
      (config) => {
        const storedToken = localStorage.getItem('token');
        if (storedToken) {
          config.headers.Authorization = `Bearer ${storedToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for 401 errors
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Only logout on 401 from auth endpoints, not from all endpoints
        if (error.response?.status === 401) {
          const url = error.config?.url || '';
          const detail = typeof error.response?.data?.detail === 'string' ? error.response.data.detail : '';
          // Gmail/Google OAuth failures are not app-session expiry — never log out for them.
          const isGmailError = error.response?.headers?.['x-gmail-status'] === 'reconnect_required'
            || url.includes('/oauth/gmail')
            || /gmail|mailbox|google/i.test(detail);
          // Don't logout if it's a public endpoint or signature endpoint
          if (!isGmailError && !url.includes('/sign/') && !url.includes('/public/') && !url.includes('/health')) {
            console.log('AuthContext: 401 received, logging out', url);
            logout('expired');
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(interceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // Check if token is valid on mount
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('token');
      console.log('AuthContext: Checking auth, token exists:', !!storedToken);
      if (storedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          console.log('AuthContext: Auth check successful', response.data);
          setUser(response.data);
          setToken(storedToken);
          
          // Check if viewing as another user
          if (response.data.viewing_as) {
            setViewingAs(response.data.viewing_as);
          } else {
            setViewingAs(null);
          }
          
          // Check if user must change password
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            const parsedUser = JSON.parse(storedUser);
            if (parsedUser.must_change_password) {
              setMustChangePassword(true);
            }
          }
        } catch (error) {
          console.error('AuthContext: Auth check failed', error.response?.status, error.message);
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  // Activity tracking for session timeout
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    setLastActivity(Date.now());
    setShowTimeoutWarning(false);
  }, []);

  // Set up activity listeners
  useEffect(() => {
    if (!token) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, [token, updateActivity]);

  // Session timeout check
  useEffect(() => {
    if (!token) return;

    const checkTimeout = setInterval(() => {
      const timeSinceActivity = Date.now() - lastActivity;
      
      if (timeSinceActivity >= SESSION_TIMEOUT) {
        // Session expired - logout
        logout('inactivity');
      } else if (timeSinceActivity >= SESSION_TIMEOUT - WARNING_BEFORE_TIMEOUT) {
        // Show warning
        setShowTimeoutWarning(true);
      }
    }, 10000); // Check every 10 seconds

    return () => clearInterval(checkTimeout);
  }, [token, lastActivity]);

  // Silently refresh the access token so an ACTIVE user never hits the
  // backend's hard 15-minute token expiry. The idle-logout effect above still
  // signs the user out after SESSION_TIMEOUT of genuine inactivity (HIPAA).
  const refreshAccessToken = useCallback(async () => {
    try {
      const res = await axios.post(`${API}/auth/refresh`);
      const newToken = res.data?.token;
      if (newToken) {
        localStorage.setItem('token', newToken);
        setToken(newToken);
      }
    } catch (e) {
      // If refresh fails (token already expired), the 401 interceptor logs out.
    }
  }, []);

  useEffect(() => {
    if (!token) return;
    const REFRESH_INTERVAL = 5 * 60 * 1000; // every 5 min; token lives 15 min
    const id = setInterval(() => {
      // Only keep the session alive while the user is genuinely active.
      if (Date.now() - lastActivityRef.current < SESSION_TIMEOUT) {
        refreshAccessToken();
      }
    }, REFRESH_INTERVAL);
    return () => clearInterval(id);
  }, [token, refreshAccessToken]);

  const login = async (email, password) => {
    console.log('AuthContext: Attempting login for', email);
    const response = await axios.post(`${API}/auth/login`, { email, password });
    console.log('AuthContext: Login response', response.data.success);
    
    if (response.data.success) {
      console.log('AuthContext: Storing token and setting user');
      localStorage.setItem('token', response.data.token);
      setToken(response.data.token);
      lastActivityRef.current = Date.now();
      setLastActivity(Date.now());

      // Set user immediately from the login payload so ProtectedRoute has
      // permissions right away (no null-user race), then enrich with the full
      // profile (tour_welcome_seen, etc.) from /auth/me.
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setUser(response.data.user);
      try {
        const meRes = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${response.data.token}` },
        });
        const fullUser = { ...response.data.user, ...meRes.data };
        localStorage.setItem('user', JSON.stringify(fullUser));
        setUser(fullUser);
        if (fullUser?.must_change_password) {
          setMustChangePassword(true);
          return { success: true, mustChangePassword: true };
        }
      } catch (e) { /* fall back to login payload */ }

      // Check if user must change password (from login payload)
      if (response.data.user?.must_change_password) {
        setMustChangePassword(true);
        return { success: true, mustChangePassword: true };
      }
      
      return { success: true };
    }
    
    return { success: false, message: response.data.message };
  };

  const clearMustChangePassword = () => {
    setMustChangePassword(false);
    // Update the stored user object
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      parsedUser.must_change_password = false;
      localStorage.setItem('user', JSON.stringify(parsedUser));
    }
  };

  const logout = async (reason) => {
    try {
      if (token) {
        await axios.post(`${API}/auth/logout`);
      }
    } catch (error) {
      // Ignore logout errors
    }
    // Capture the URL the user was on so we can send them back after re-login.
    // Skip the auth pages themselves to avoid an infinite redirect loop.
    try {
      const path = window.location.pathname + window.location.search;
      if (path && !path.startsWith('/login') && path !== '/') {
        localStorage.setItem('post_login_redirect', path);
      }
    } catch (e) { /* ignore */ }
    // Stash the reason so /login can render a friendly banner explaining
    // why the user landed here (only set for auto-logouts, not manual ones).
    try {
      if (reason === 'inactivity' || reason === 'expired') {
        localStorage.setItem('logout_reason', reason);
      }
    } catch (e) { /* ignore */ }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('viewing_as');
    setToken(null);
    setUser(null);
    setViewingAs(null);
    setShowTimeoutWarning(false);
  };

  const exitViewAs = async () => {
    try {
      await axios.post(`${API}/users/exit-view-as`);
      setViewingAs(null);
      localStorage.removeItem('viewing_as');
      // Reload to refresh permissions
      window.location.reload();
    } catch (error) {
      console.error('Failed to exit view-as mode:', error);
    }
  };

  const extendSession = () => {
    lastActivityRef.current = Date.now();
    setLastActivity(Date.now());
    setShowTimeoutWarning(false);
    // Immediately renew the token so "Stay logged in" truly extends the session.
    refreshAccessToken();
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token,
    login,
    logout,
    showTimeoutWarning,
    extendSession,
    updateActivity,
    mustChangePassword,
    clearMustChangePassword,
    viewingAs,
    exitViewAs
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
